import { FC, useMemo, useState } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'

import { PERMISSIONS } from '@forto-dev/auth-sdk-models'
import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupItem,
  Button,
  Card,
  Cluster,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Icon,
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Typography,
} from '@forto-dev/phoenix-ui-core'

import { Access, useAuth } from '@/features/auth'
import { getInitialsForName } from '@/utils/initials'
import { useLocalisation } from '@/utils/localisation'

import { AddSubscriber } from './AddSubscriber'
import { useSubscriptionDelete } from '../api/useSubscriptionDelete'
import { entitySubscriptionsQueryOptions } from '../api/useSubscriptions'

export type SubscriptionsProps = {
  id: string
}

export const Subscriptions: FC<SubscriptionsProps> = ({ id }) => {
  const { isCustomerActive } = useAuth()
  const [subscriptionsDialogOpen, setSubscriptionsDialogOpen] = useState<boolean>(false)
  const { translate } = useLocalisation()
  const subscriptions = useSuspenseQuery(entitySubscriptionsQueryOptions(id))
  const deleteMutation = useSubscriptionDelete()
  const avatars: AvatarGroupItem[] = useMemo(
    () =>
      [...subscriptions.data.users, ...subscriptions.data.freighties].map((user) => ({
        id: user.id,
        name: user.name,
        fallback: getInitialsForName(user.name),
      })),
    [subscriptions.data.freighties, subscriptions.data.users]
  )
  const subscribedUserIds = useMemo(() => subscriptions.data.users.map((user) => user.id), [subscriptions.data.users])

  return (
    <Cluster gap={16} y="center">
      {avatars.length > 0 ? (
        <AvatarGroup
          items={avatars}
          size="lg"
          max={2}
          onMoreOptionsClick={isCustomerActive ? () => setSubscriptionsDialogOpen(true) : undefined}
        />
      ) : (
        <Access permission={PERMISSIONS.SHIPMENTS.SUBSCRIBERS.UPDATE}>
          <Tooltip>
            <TooltipTrigger>
              <Button
                variant="outline-secondary"
                size="icon"
                data-testid="add-subscribers"
                onClick={() => setSubscriptionsDialogOpen(true)}
              >
                <Icon name="Plus" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{translate('subscriptions.add')}</TooltipContent>
          </Tooltip>
        </Access>
      )}

      <Dialog open={subscriptionsDialogOpen} onOpenChange={setSubscriptionsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate('subscriptions.dialog.title')}</DialogTitle>
          </DialogHeader>
          <Typography variant="body">{translate('subscriptions.dialog.intro')}</Typography>
          <Access permission={PERMISSIONS.SHIPMENTS.SUBSCRIBERS.UPDATE}>
            <AddSubscriber id={id} subscribedUserIds={subscribedUserIds} />
          </Access>
          <DialogBody>
            <Stack gap={16}>
              <Stack>
                {subscriptions.data.users.map((user) => (
                  <Card key={user.id}>
                    <Cluster gap={16} x="between">
                      <Cluster gap={16} y="center">
                        <Avatar>
                          <AvatarFallback>{getInitialsForName(user.name)}</AvatarFallback>
                        </Avatar>
                        <Stack gap={4}>
                          <Typography>{user.name}</Typography>
                          <Typography variant="small" color="muted">
                            {user.email}
                          </Typography>
                        </Stack>
                      </Cluster>
                      <Access permission={PERMISSIONS.SHIPMENTS.SUBSCRIBERS.UPDATE}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={async () => {
                            await deleteMutation.mutateAsync({
                              entityId: id,
                              userId: user.id,
                            })
                          }}
                        >
                          <Icon name="Bin" color="negative" />
                        </Button>
                      </Access>
                    </Cluster>
                  </Card>
                ))}
                {subscriptions.data.freighties.map((freightie) => (
                  <Card key={freightie.id}>
                    <Cluster gap={16} y="center">
                      <Avatar>
                        <AvatarFallback>{getInitialsForName(freightie.name)}</AvatarFallback>
                      </Avatar>
                      <Stack gap={4}>
                        <Typography>{freightie.name}</Typography>
                        <Typography variant="small" color="muted">
                          {freightie.email}
                        </Typography>
                      </Stack>
                    </Cluster>
                  </Card>
                ))}
              </Stack>
            </Stack>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </Cluster>
  )
}
